import React, { forwardRef } from 'react';
import PropTypes from 'prop-types';
import Icon from 'app/components/atoms/Icon/Icon';
import { getFilterConfig } from '../GlobalSearchModal.filterConfig';
import { isNullOperator } from '../GlobalSearchModal.constants';
import {
    SearchInputContainer,
    SearchInputWrapper,
    SearchTokensContainer,
    SearchTokenGroup,
    FilterChip,
    OperatorChip,
    ValueChip,
    BuildingTokenGroup,
    BuildingChip,
    SearchIconAdornment,
    MobileCloseButton,
    StyledInput,
} from '../GlobalSearchModal.styles';

const getTokenDisplayValue = (token) => {
    // For null operators, always return null (no value to display)
    if (isNullOperator(token.operator)) {
        return null;
    }
    
    if (!token.value) return null;
    
    if (token.filter?.key) {
        const filterConfig = getFilterConfig(token.filter.key);
        if (filterConfig?.formatDisplayValue) {
            return filterConfig.formatDisplayValue(token.value);
        }
    }
    
    if (typeof token.value === 'object' && token.value.displayName) {
        return token.value.displayName;
    }
    
    return token.value;
}

const TokenChips = ({ token, index, onRemove, removable }) => {
    const onDelete = removable ? () => onRemove(index) : undefined;

    // Free text token
    if (!token.filter) {
        return (
            <SearchTokenGroup>
                <ValueChip label={token.value} onDelete={onDelete} size="small" />
            </SearchTokenGroup>
        );
    }

    const displayValue = getTokenDisplayValue(token);
    const filterConfig = token.filter ? getFilterConfig(token.filter.key) : null;
    const shouldShowOperator = token.operator && !(filterConfig?.skipOperator);
    
    // Only show value chip if:
    // 1. Not a null operator (null operators don't need values)
    // 2. displayValue exists and is not empty
    const shouldShowValue = !isNullOperator(token.operator) && displayValue != null && displayValue !== '';

    return (
        <SearchTokenGroup>
            <FilterChip label={token.filter.text} size="small" />
            {shouldShowOperator && <OperatorChip label={token.operator} size="small" />}
            {shouldShowValue && (
                <ValueChip label={displayValue} onDelete={onDelete} size="small" />
            )}
        </SearchTokenGroup>
    );
};

const BuildingToken = ({ currentFilter, currentOperator, searchQuery, currentInputState }) => {
    if (!currentFilter) return null;
    
    const filterConfig = getFilterConfig(currentFilter.key);
    const shouldShowOperator = currentOperator && !(filterConfig?.skipOperator);
    
    return (
        <BuildingTokenGroup>
            <BuildingChip label={currentFilter.text} size="small" />
            {shouldShowOperator && <BuildingChip label={currentOperator} size="small" />}
        </BuildingTokenGroup>
    );
}

const SearchInput = forwardRef(function SearchInput(props, ref) {
    const {
        searchQuery,
        searchTokens,
        currentFilter,
        currentOperator,
        currentInputState,
        placeholder,
        onQueryChange,
        onKeyDown,
        onFocus,
        onBlur,
        onRemoveToken,
        onClose,
        onContainerClick,
        nonRemovableTokenIndices = [],
    } = props;

    const isRemovable = (index) => !nonRemovableTokenIndices.includes(index);

    return (
        <SearchInputContainer>
            <SearchInputWrapper onClick={onContainerClick}>
                <SearchIconAdornment>
                    <Icon name="magnify" size="md" />
                </SearchIconAdornment>
                
                <SearchTokensContainer>
                    {searchTokens.map((token, index) => (
                        <TokenChips
                            key={index}
                            token={token}
                            index={index}
                            onRemove={onRemoveToken}
                            removable={isRemovable(index)}
                        />
                    ))}
                    
                    <BuildingToken
                        currentFilter={currentFilter}
                        currentOperator={currentOperator}
                        searchQuery={searchQuery}
                        currentInputState={currentInputState}
                    />
                    
                    <StyledInput
                        ref={ref}
                        type="text"
                        placeholder={placeholder}
                        value={searchQuery}
                        onChange={(e) => onQueryChange(e.target.value)}
                        onKeyDown={onKeyDown}
                        onFocus={onFocus}
                        onBlur={onBlur}
                    />
                </SearchTokensContainer>
                
                <MobileCloseButton
                    onClick={(e) => {
                        e.stopPropagation();
                        onClose();
                    }}
                >
                    <Icon name="close" size="sm" />
                </MobileCloseButton>
            </SearchInputWrapper>
        </SearchInputContainer>
    );
});

SearchInput.propTypes = {
    searchQuery: PropTypes.string.isRequired,
    searchTokens: PropTypes.array.isRequired,
    currentFilter: PropTypes.object,
    currentOperator: PropTypes.string,
    currentInputState: PropTypes.string.isRequired,
    placeholder: PropTypes.string,
    onQueryChange: PropTypes.func.isRequired,
    onKeyDown: PropTypes.func.isRequired,
    onFocus: PropTypes.func.isRequired,
    onBlur: PropTypes.func.isRequired,
    onRemoveToken: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
    onContainerClick: PropTypes.func.isRequired,
    nonRemovableTokenIndices: PropTypes.arrayOf(PropTypes.number),
};

SearchInput.defaultProps = {
    placeholder: 'Search...',
    nonRemovableTokenIndices: [],
};

export default SearchInput;

